import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Grid,
  Pagination,
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../firebase";

const PAGE_SIZE = 5;

function Matoshrivehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState({
    type: "",
    vehicalNo: "",
    puc: "",
    insurance: "",
    pucDate: "",
    insuranceDate: "",
  });

  // ================= Load vehicles =================
  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    const snap = await getDocs(collection(db, "matoshrivehicles"));
    const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setVehicles(arr);
  };

  // ================= Form change handler =================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ================= Save / Update vehicle =================
  const saveVehicle = async () => {
    // Validation
    if (!form.type || !form.vehicalNo ) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    try {
      let id = editingId || doc(collection(db, "matoshrivehicles")).id;

      await setDoc(doc(db, "matoshrivehicles", id), {
        ...form,
        createdTime: editingId ? form.createdTime : Timestamp.now(),
      });

      Swal.fire("Success", editingId ? "Vehicle updated" : "Vehicle added", "success");

      setForm({
        type: "",
        vehicalNo: "",
        puc: "",
        insurance: "",
        pucDate: "",
        insuranceDate: "",
      });
      setEditingId(null);
      loadVehicles();
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  // ================= Edit vehicle =================
  const editVehicle = (v) => {
    setForm(v);
    setEditingId(v.id);
  };

  // ================= Delete vehicle =================
  const deleteVehicle = async (id) => {
    const res = await Swal.fire({
      title: "Delete vehicle?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!res.isConfirmed) return;

    await deleteDoc(doc(db, "matoshrivehicles", id));
    Swal.fire("Deleted", "Vehicle removed", "success");
    loadVehicles();
  };

  // ================= Filter & Pagination =================
  const filtered = vehicles.filter(
    (v) =>
      v.type?.toLowerCase().includes(search.toLowerCase()) ||
      v.vehicalNo?.toLowerCase().includes(search.toLowerCase())
  );

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Box flex={1} p={3}>
        <Typography variant="h5" fontWeight={800} mb={2}>
          Matoshri Vehicle Management
        </Typography>

        {/* ================= Form ================= */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={2}>
              {editingId ? "Edit Vehicle" : "Add New Vehicle"}
            </Typography>

            <Grid className="row" spacing={2}>
              <Grid className="col-sm-3 mb-3">
                <TextField
                  fullWidth
                  size="small"
                  label="Type"
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid className="col-sm-3 mb-3">
                <TextField
                  fullWidth
                  size="small"
                  label="Vehical No"
                  name="vehicalNo"
                  value={form.vehicalNo}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid className="col-sm-3 mb-3">
                <TextField
                  fullWidth
                  size="small"
                  label="PUC"
                  name="puc"
                  value={form.puc}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid className="col-sm-3 mb-3">
                <TextField
                  fullWidth
                  size="small"
                  label="Insurance"
                  name="insurance"
                  value={form.insurance}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid className="col-sm-3 mb-3">
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="PUC Expiry Date"
                  name="pucDate"
                  InputLabelProps={{ shrink: true }}
                  value={form.pucDate}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid className="col-sm-3 mb-3">
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Insurance Expiry Date"
                  name="insuranceDate"
                  InputLabelProps={{ shrink: true }}
                  value={form.insuranceDate}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid className="col-sm-3 mb-3">
                <Button variant="contained" onClick={saveVehicle}>
                  {editingId ? "Update Vehicle" : "Add Vehicle"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ================= Search ================= */}
        <TextField
          label="Search by type or vehical no"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          size="small"
        />

        {/* ================= Table ================= */}
        <Card>
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sr No</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Vehical No</TableCell>
                  <TableCell>PUC</TableCell>
                  <TableCell>Insurance</TableCell>
                  <TableCell>PUC Expiry Date</TableCell>
                  <TableCell>Insurance Expiry Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.map((v, index) => (
                  <TableRow key={v.id}>
                    <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                    <TableCell>{v.type}</TableCell>
                    <TableCell>{v.vehicalNo}</TableCell>
                    <TableCell>{v.puc}</TableCell>
                    <TableCell>{v.insurance}</TableCell>
                    <TableCell>{v.pucDate}</TableCell>
                    <TableCell>{v.insuranceDate}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => editVehicle(v)}>
                        <EditOutlined />
                      </IconButton>
                      <IconButton color="error" onClick={() => deleteVehicle(v.id)}>
                        <DeleteOutline />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(filtered.length / PAGE_SIZE)}
                page={page}
                onChange={(e, v) => setPage(v)}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Matoshrivehicles;